import {
    Alert,
    Box,
    Button,
    LinearProgress,
    Stack,
    Tab,
    Tabs,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { LineChart, Pencil, Sigma } from 'lucide-react';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import ReviewStatus from '~/components/Structures/ReviewStatus';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { doseOriginLabelOverrideState } from '~/state/atoms/dose/doseAtoms';
import {
    replanningCompareViewState,
    replanningDvhComparisonOpenState,
    replanningEditingStructuresState,
    replanningReviewActiveState,
    replanningRunOutputsState,
} from '~/state/atoms/adaptiveReplanningAtoms';
import { structureSetReadModelFamily } from '~/state/atoms/structureSet/structureSetAtoms';
import ReviewContent from '../ReviewStep';
import DoseSumDialog from './DoseSumDialog';
import DvhComparisonPanel from './DvhComparisonPanel';
import { REPLANNING_ALERT_SX } from './replanningAlertSx';
import ReviewDoseSection from './ReviewDoseSection';
import ReviewRegistrationsPanel from './ReviewRegistrationsPanel';
import ReviewTransformSection from './ReviewTransformSection';
import StructureSetApprovalButtons from './StructureSetApprovalButtons';
import { useDiscardRigidStructures } from './useDiscardRigidStructures';
import { useReconcilePropagatedStructures } from './useReconcilePropagatedStructures';
import { useReferenceComparison } from './useReferenceComparison';
import { useReviewDatasets } from './useReviewDatasets';
import { useReviewTargetDose } from './useReviewTargetDose';
import { useReviewTransforms } from './useReviewTransforms';
import { useRigidPlanningStructures } from './useRigidPlanningStructures';
import { useSelectPropagatedStructureSet } from './useSelectPropagatedStructureSet';

type ReviewTab = 'registrations' | 'structures' | 'dose';
// Clinical order: approve the registration, then the structures, then review the dose.
const TABS: readonly ReviewTab[] = ['registrations', 'structures', 'dose'];

const TOOL_SX = {
    height: 26,
    px: 1,
    fontSize: 11,
    whiteSpace: 'nowrap',
    '& .MuiButton-startIcon': { mr: 0.5 },
} as const;

const TABS_SX = {
    minHeight: 32,
    borderBottom: '1px solid',
    borderColor: 'divider',
    // One fixed weight in every state: the three tabs keep their size when selected, so nothing shifts.
    '& .MuiTab-root': {
        minHeight: 32,
        minWidth: 0,
        py: 0,
        fontSize: 11.5,
        fontWeight: 500,
        textTransform: 'none',
    },
    '& .MuiTab-root.Mui-selected': { fontWeight: 500 },
} as const;

const VIEW_TOGGLE_SX = {
    '& .MuiToggleButton-root': {
        flex: '1 1 0',
        minWidth: 0,
        height: 26,
        px: 1,
        py: 0,
        fontSize: 11,
        fontWeight: 500,
        lineHeight: 1,
        textTransform: 'none',
    },
} as const;

const ALERT_SX = { ...REPLANNING_ALERT_SX, mx: 0 };

export default function ReviewStage() {
    const intl = useIntl();
    // The run's own set on the run's target scan, whichever scan is displayed.
    const { propagatedSet } = useReviewDatasets();
    const propagatedReadModel = useRecoilValue(
        structureSetReadModelFamily(propagatedSet?.dicomUid ?? '')
    );
    const propagated = propagatedSet ? propagatedReadModel : undefined;
    const { pendingPropagatedUid } = useRecoilValue(replanningRunOutputsState);
    useReferenceComparison();
    useReconcilePropagatedStructures();
    const transforms = useReviewTransforms();
    const registrationsApproved =
        transforms.options.length > 0 &&
        transforms.options.every(
            (option) =>
                option.registration?.approvalStatus ===
                DICOMApprovalStatus.APPROVED
        );
    const kind = transforms.selected?.kind;
    const rigidOption = transforms.options.find(
        (option) => option.kind === 'rigid'
    );
    const rigid = useRigidPlanningStructures(rigidOption, kind === 'rigid');
    const target = useReviewTargetDose(transforms.selected, rigid.structureSet);
    const discardRigid = useDiscardRigidStructures();
    const [dvhOpen, setDvhOpen] = useRecoilState(
        replanningDvhComparisonOpenState
    );
    const [comparing, setComparing] = useRecoilState(
        replanningCompareViewState
    );
    const setDoseOriginOverride = useSetRecoilState(
        doseOriginLabelOverrideState
    );
    const setEditingStructures = useSetRecoilState(
        replanningEditingStructuresState
    );
    const setReviewActive = useSetRecoilState(replanningReviewActiveState);
    const [sumOpen, setSumOpen] = useState(false);
    const [tab, setTab] = useState<ReviewTab>('registrations');
    useSelectPropagatedStructureSet();

    // Side by side by default; off when Review closes.
    useEffect(() => {
        setComparing(true);
        return () => setComparing(false);
    }, [setComparing]);

    // Flags the Review step as on screen while it is mounted.
    useEffect(() => {
        setReviewActive(true);
        return () => setReviewActive(false);
    }, [setReviewActive]);

    // The target dose is carried from the prior plan, so it is labelled propagated, not AI generated.
    useEffect(() => {
        setDoseOriginOverride(
            intl.formatMessage({ id: 'replanning.dose.origin' })
        );
        return () => setDoseOriginOverride(undefined);
    }, [setDoseOriginOverride, intl]);

    // The rigidly moved structures exist for this view only.
    useEffect(() => discardRigid, [discardRigid]);

    if (!propagated) {
        return (
            <Typography
                px={1.5}
                py={1}
                fontSize={11.5}
                color="text.secondary"
                data-testid={
                    pendingPropagatedUid
                        ? 'replanning-completing-set'
                        : 'replanning-nothing-propagated'
                }
            >
                <FormattedMessage
                    id={
                        pendingPropagatedUid
                            ? 'replanning.review.completingSet'
                            : 'replanning.review.nothingPropagated'
                    }
                />
            </Typography>
        );
    }

    const isLocked =
        propagated.approvalStatus !== DICOMApprovalStatus.UNAPPROVED;
    const structuresApproved =
        propagated.approvalStatus === DICOMApprovalStatus.APPROVED;
    const structuresGate = registrationsApproved
        ? undefined
        : 'replanning.review.gate.registrations';
    const doseGate =
        structuresApproved && registrationsApproved
            ? undefined
            : 'replanning.review.gate.structures';

    return (
        <Box
            height="100%"
            minHeight={0}
            display="flex"
            flexDirection="column"
            data-testid="replanning-review-stage"
        >
            <Box flexShrink={0} px={1.5} pt={1} pb={0.75}>
                <Typography
                    fontSize={11}
                    fontWeight={600}
                    color="text.secondary"
                    sx={{
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                        mb: 0.5,
                    }}
                >
                    <FormattedMessage id="replanning.review.visualization" />
                </Typography>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    fullWidth
                    value={comparing ? 'sideBySide' : 'targetOnly'}
                    onChange={(_, value) =>
                        value && setComparing(value === 'sideBySide')
                    }
                    aria-label={intl.formatMessage({
                        id: 'replanning.review.view',
                    })}
                    sx={VIEW_TOGGLE_SX}
                    data-testid="replanning-review-view-toggle"
                >
                    <ToggleButton
                        value="sideBySide"
                        data-testid="replanning-review-view-sidebyside"
                    >
                        <FormattedMessage id="replanning.review.view.sideBySide" />
                    </ToggleButton>
                    <ToggleButton
                        value="targetOnly"
                        data-testid="replanning-review-view-target"
                    >
                        <FormattedMessage id="replanning.review.view.targetOnly" />
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Tabs
                value={tab}
                onChange={(_, value: ReviewTab) => setTab(value)}
                variant="fullWidth"
                aria-label={intl.formatMessage({
                    id: 'replanning.review.tabs',
                })}
                sx={[TABS_SX, { flexShrink: 0 }]}
            >
                {TABS.map((id) => (
                    <Tab
                        key={id}
                        value={id}
                        label={
                            <FormattedMessage
                                id={`replanning.review.tab.${id}`}
                            />
                        }
                        id={`replanning-review-tab-${id}`}
                        aria-controls="replanning-review-panel"
                        data-testid={`replanning-review-tab-${id}`}
                    />
                ))}
            </Tabs>

            <Box
                flex={1}
                minHeight={0}
                overflow="auto"
                role="tabpanel"
                id="replanning-review-panel"
                aria-labelledby={`replanning-review-tab-${tab}`}
                data-testid="replanning-review-structures"
            >
                {tab === 'structures' &&
                    (kind === 'rigid' ? (
                        <Stack px={1.5} py={1} spacing={0.75}>
                            {target.drawnKind === 'rigid' ? (
                                <Typography
                                    fontSize={11.5}
                                    color="text.secondary"
                                    data-testid="replanning-rigid-structures-note"
                                >
                                    <FormattedMessage id="replanning.review.rigidStructures.note" />
                                </Typography>
                            ) : (
                                <Alert
                                    severity="warning"
                                    variant="outlined"
                                    sx={ALERT_SX}
                                    data-testid="replanning-rigid-not-shown"
                                >
                                    <FormattedMessage id="replanning.review.rigidStructures.notShown" />
                                </Alert>
                            )}
                            {target.status?.status === 'failed' && (
                                <Alert
                                    severity="error"
                                    variant="outlined"
                                    sx={ALERT_SX}
                                    data-testid="replanning-rigid-dose-failed"
                                >
                                    <FormattedMessage id="replanning.review.rigidDose.failed" />
                                </Alert>
                            )}
                            {rigid.status?.status === 'building' && (
                                <Box data-testid="replanning-rigid-structures-building">
                                    <Typography
                                        fontSize={11}
                                        color="text.secondary"
                                    >
                                        <FormattedMessage id="replanning.review.rigidStructures.building" />
                                    </Typography>
                                    <LinearProgress sx={{ mt: 0.5 }} />
                                </Box>
                            )}
                            {rigid.status?.status === 'failed' && (
                                <Alert
                                    severity="error"
                                    variant="outlined"
                                    sx={ALERT_SX}
                                    data-testid="replanning-rigid-structures-failed"
                                >
                                    <FormattedMessage
                                        id={rigid.status.messageId}
                                    />
                                </Alert>
                            )}
                            {rigid.skippedRoiCount > 0 && (
                                <Alert
                                    severity="warning"
                                    variant="outlined"
                                    sx={ALERT_SX}
                                    data-testid="replanning-rigid-structures-skipped"
                                >
                                    <FormattedMessage
                                        id="replanning.review.rigidStructures.skipped"
                                        values={{
                                            count: rigid.skippedRoiCount,
                                        }}
                                    />
                                </Alert>
                            )}
                        </Stack>
                    ) : (
                        // Unwrapped: an auto-height wrapper would collapse its definite-height layout.
                        <ReviewContent showHeader={false} showImages={false} />
                    ))}
                {tab === 'dose' && (
                    <Stack px={1.5} py={1} spacing={1.25}>
                        <ReviewDoseSection
                            target={target}
                            kind={kind}
                            disabledReasonId={doseGate}
                        />
                        <Stack direction="row" spacing={0.5}>
                            <Button
                                size="small"
                                variant={dvhOpen ? 'contained' : 'outlined'}
                                disableElevation
                                startIcon={<LineChart size={13} />}
                                onClick={() => setDvhOpen((on) => !on)}
                                aria-pressed={dvhOpen}
                                data-testid="replanning-open-dvh-comparison"
                                sx={[TOOL_SX, { flex: 1 }]}
                            >
                                <FormattedMessage id="replanning.review.tool.dvh" />
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Sigma size={13} />}
                                onClick={() => setSumOpen(true)}
                                data-testid="replanning-open-dose-sum"
                                sx={[TOOL_SX, { flex: 1 }]}
                            >
                                <FormattedMessage id="replanning.review.tool.sum" />
                            </Button>
                        </Stack>
                    </Stack>
                )}
                {tab === 'registrations' && (
                    <Stack px={1.5} py={1} spacing={1}>
                        <ReviewTransformSection transforms={transforms} />
                        {transforms.options.length > 0 && (
                            <ReviewRegistrationsPanel transforms={transforms} />
                        )}
                    </Stack>
                )}
            </Box>

            {/* Structure-set sign-off belongs to the structures tab; the
                registrations and dose tabs carry their own approval controls. */}
            {tab === 'structures' && (
                <Stack
                    flexShrink={0}
                    px={1.5}
                    py={1}
                    spacing={0.75}
                    borderTop="1px solid"
                    borderColor="divider"
                    data-testid="replanning-review-approval"
                >
                    {!isLocked && (
                        <Button
                            size="small"
                            fullWidth
                            variant="outlined"
                            startIcon={<Pencil size={13} />}
                            onClick={() => setEditingStructures(true)}
                            data-testid="replanning-edit-structures"
                            sx={TOOL_SX}
                        >
                            <FormattedMessage id="replanning.review.editStructures" />
                        </Button>
                    )}
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={1}
                    >
                        <Box minWidth={0}>
                            <Typography fontSize={11} color="text.secondary">
                                <FormattedMessage id="replanning.review.approval.title" />
                            </Typography>
                            <ReviewStatus
                                status={propagated.approvalStatus}
                                fontSize={12}
                            />
                        </Box>
                        <StructureSetApprovalButtons
                            dicomUid={propagated.dicomUid}
                            approvalStatus={propagated.approvalStatus}
                            disabledReasonId={structuresGate}
                        />
                    </Box>
                    {isLocked && (
                        <Alert
                            severity="info"
                            variant="outlined"
                            sx={ALERT_SX}
                            data-testid="replanning-structures-locked"
                        >
                            <FormattedMessage id="replanning.review.locked" />
                        </Alert>
                    )}
                </Stack>
            )}

            <DvhComparisonPanel />
            <DoseSumDialog
                open={sumOpen}
                onClose={() => setSumOpen(false)}
                priorOnTarget={target.dose}
                targetStructures={target.structureSet}
            />
        </Box>
    );
}
